import React, { useContext, useEffect, useState } from 'react';
import { CompanyInfoInDTO, FeaturesEnum, PermissionTypesEnum, UserCompanyOutDTO } from '@api/ringil-api.v2';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent';
import { ApiContext } from '@api/api';
import useTranslation from '@hooks/i18n/useTranslation.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import CrnBadge from '@components/ringil3/Badges/CrnBadge.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import IDBadge from '@components/ringil3/Badges/IDBadge.tsx';
import SvgUsers from '@icons/id.svg?react';
import SvgCatalogs from '@icons/list.svg?react';
import Tabs, { TabProps } from '@components/ringil3/RingilTabs/Tabs.tsx';
import CompanyProfileUsers from '@app/pages/customer/CompanyProfile/CompanyProfileUsers.tsx';
import { useEditCompanyNameSidebar } from '@app/pages/customer/CompanyProfile/sidebar/EditCompanyNameSidebar.tsx';
import AddressTag from '@components/framework/tags/AddressTag.tsx';
import CompanyProfileCatalogs from '@app/pages/customer/CompanyProfile/CompanyProfileCatalogs.tsx';
import CompanyProfileSchenkerCatalogs from '@app/pages/customer/CompanyProfile/CompanyProfileSchenkerCatalogs.tsx';
import CompanyProfileFedex from '@app/pages/customer/CompanyProfile/CompanyProfileFedex.tsx';
import CompanyProfileDhl from '@app/pages/customer/CompanyProfile/CompanyProfileDhl.tsx';
import CompanyProfileFolders from '@app/pages/customer/CompanyProfile/CompanyProfileFolders.tsx';
import SvgFolder from '@icons/folder.svg?react';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';

type ComponentProps = unknown

export const CompanyProfile: React.FC<ComponentProps> = () => {
  const { tr } = useTranslation();
  const { getCompanyForCurrentUser, updateCompanyInfo } = useContext(ApiContext);

  const editCompanyFlag = useSecuredFlag('edit-company-info', {
    kind: 'editability',
    description: 'Edit company name and address',
    permissions: [PermissionTypesEnum.PERMISSION_SETTINGS_COMPANY_PROFILE],
  });
  const showCatalogsFlag = useSecuredFlag('show-catalogs-tab', {
    kind: 'visibility',
    description: 'See company catalogs tab',
    permissions: [PermissionTypesEnum.PERMISSION_ADMIN_CODELISTS],
  });
  const showSchenkerFlag = useSecuredFlag('show-schenker-catalogs-tab', {
    kind: 'visibility',
    description: 'See Schenker catalogs tab (codelists permission + Schenker feature)',
    permissions: [PermissionTypesEnum.PERMISSION_ADMIN_CODELISTS],
    features: [FeaturesEnum.SCHENKER_API],
  });
  const showFedexFlag = useSecuredFlag('show-fedex-tab', {
    kind: 'visibility',
    description: 'See FedEx account onboarding tab (company profile permission + FedEx feature)',
    permissions: [PermissionTypesEnum.PERMISSION_SETTINGS_COMPANY_PROFILE],
    features: [FeaturesEnum.FEDEX_API],
  });
  const showFoldersFlag = useSecuredFlag('show-folders-tab', {
    kind: 'visibility',
    description: 'Manage company folders and their visibility (RD-2977)',
    permissions: [PermissionTypesEnum.PERMISSION_COMPANY_FOLDERS_RW],
  });
  const showDhlFlag = useSecuredFlag('show-dhl-tab', {
    kind: 'visibility',
    description: 'See DHL Express API credentials tab (company profile permission + DHL feature)',
    permissions: [PermissionTypesEnum.PERMISSION_SETTINGS_COMPANY_PROFILE],
    features: [FeaturesEnum.DHL_API],
  });

  const editCompanyEnabled = editCompanyFlag.granted;
  const catalogsAllowed = showCatalogsFlag.granted;
  const schenkerEnabled = showSchenkerFlag.granted;
  const fedexEnabled = showFedexFlag.granted;
  const dhlEnabled = showDhlFlag.granted;
  const foldersEnabled = showFoldersFlag.granted;

  // Tabs are keyed by name: headers are filtered by permissions/features, so a positional
  // index would point at the wrong (hidden) content when e.g. only Users + FedEx are visible.
  type CompanyProfileTab = 'users' | 'catalogs' | 'schenker' | 'fedex' | 'dhl' | 'folders';
  const [activeTab, setActiveTab] = useState<CompanyProfileTab>('users');
  const [companyData, setCompanyData] = useState<UserCompanyOutDTO>();

  const { setVisible: setVisibleCompanyNameEditSidebar, EditCompanyNameSidebar } = useEditCompanyNameSidebar();

  const load = () => {
    getCompanyForCurrentUser(data => {
      setCompanyData(data);
    });
  };

  useEffect(() => {
    load();
  }, []);

  const visibleTabs: { key: CompanyProfileTab; tab: TabProps }[] = [
    {
      key: 'users',
      tab: { label: tr(`CompanyProfile.tabUsers`, `Users`), active: activeTab === 'users', icon: <SvgUsers /> },
    },
    ...(catalogsAllowed
      ? [{ key: 'catalogs' as const, tab: { label: tr(`CompanyProfile.tabCatalogs`, `Catalogs`), active: activeTab === 'catalogs', icon: <SvgCatalogs /> } }]
      : []),
    ...(schenkerEnabled
      ? [{ key: 'schenker' as const, tab: { label: tr(`CompanyProfile.tabSchenkerCatalogs`, `Schenker catalogs`), active: activeTab === 'schenker', icon: <SvgCatalogs /> } }]
      : []),
    ...(fedexEnabled
      ? [{ key: 'fedex' as const, tab: { label: tr(`CompanyProfile.tabFedex`, `FedEx`), active: activeTab === 'fedex', icon: <SvgCatalogs /> } }]
      : []),
    ...(dhlEnabled
      ? [{ key: 'dhl' as const, tab: { label: tr(`CompanyProfile.tabDhl`, `DHL`), active: activeTab === 'dhl', icon: <SvgCatalogs /> } }]
      : []),
    ...(foldersEnabled
      ? [{ key: 'folders' as const, tab: { label: tr(`CompanyProfile.tabFolders`, `Složky`), active: activeTab === 'folders', icon: <SvgFolder /> } }]
      : []),
  ];
  const tabsConfig: TabProps[] = visibleTabs.map(t => t.tab);

  const updateAddress = (address: { country: string; zipCode: string; city: string; streetNr: string }) => {
    const companyInfo: CompanyInfoInDTO = {
      companyName: companyData.companyName,
      country: address.country,
      zipCode: address.zipCode,
      city: address.city,
      streetNr: address.streetNr,
    };

    updateCompanyInfo(companyInfo, () => {
      load();
    });
  };

  if (!companyData) return <></>;

  return (
    <>
      <BreadCrumbComponent items={[
        {
          label: companyData?.companyName || tr(`CompanyProfile.companyProfile`, `Company profile`),
          disabled: true,
        },
      ]} />

      <SecuredArea id="company-profile-header" label="Company name + address" description="The company's name and address" editable={editCompanyFlag}>
        <Heading
          title={companyData?.companyName}
          headingVariant={'h1'}
          onEdit={editCompanyEnabled ? () => setVisibleCompanyNameEditSidebar(true) : undefined}
        />

        <Ringil3BadgeRow>
          <IDBadge id={companyData.id} />
          <CrnBadge crn={companyData.crn} />
        </Ringil3BadgeRow>
        <Ringil3BadgeRow>
          <AddressTag
            address={{
              country: companyData.country ?? '',
              zipCode: companyData.zipCode ?? '',
              city: companyData.city ?? '',
              streetNr: companyData.streetNr ?? '',
            }}
            readOnly={!editCompanyEnabled}
            onChange={address => {
              updateAddress(address);
            }}
          />
        </Ringil3BadgeRow>
      </SecuredArea>

      <Tabs
        tabs={tabsConfig}
        onTabChange={index => setActiveTab(visibleTabs[index]?.key ?? 'users')}
      />

      {activeTab === 'users' && <CompanyProfileUsers companyId={companyData.id} />}
      {activeTab === 'catalogs' && (
        <SecuredArea id="company-profile-catalogs-tab" label="Catalogs tab content" description="The company's code lists" visible={showCatalogsFlag}>
          <CompanyProfileCatalogs companyId={companyData.id} />
        </SecuredArea>
      )}
      {activeTab === 'schenker' && (
        <SecuredArea id="company-profile-schenker-tab" label="Schenker catalogs tab content" description="The company's Schenker code lists" visible={showSchenkerFlag}>
          <CompanyProfileSchenkerCatalogs companyId={companyData.id} />
        </SecuredArea>
      )}
      {activeTab === 'fedex' && (
        <SecuredArea id="company-profile-fedex-tab" label="FedEx account tab content" visible={showFedexFlag}>
          <CompanyProfileFedex companyId={companyData.id} />
        </SecuredArea>
      )}
      {activeTab === 'dhl' && (
        <SecuredArea id="company-profile-dhl-tab" label="DHL Express API credentials tab content" visible={showDhlFlag}>
          <CompanyProfileDhl companyId={companyData.id} />
        </SecuredArea>
      )}
      {activeTab === 'folders' && (
        <SecuredArea id="company-profile-folders-tab" label="Folders tab content" visible={showFoldersFlag}>
          <CompanyProfileFolders companyId={companyData.id} />
        </SecuredArea>
      )}

      <EditCompanyNameSidebar
        onChanged={() => {
          load();
        }}
      />
    </>
  );
};
